import { HierarchyNode } from 'core/API/hierarchy';
import { getAncestorIds } from 'module/onboarding/utils/hierarchyPermissions';
import { User } from 'types';

const WORKPLACE_HIERARCHY_TYPE = '/api/hierarchy_types/2';

export const getWorkplaceIdFromIri = (iri?: string | null): number | null => {
	if (!iri) return null;
	const parts = String(iri).split('/').filter(Boolean);
	const last = parts[parts.length - 1];
	const num = Number(last);
	return Number.isNaN(num) ? null : num;
};

export const findWorkplaceHierarchyNode = (
	hierarchyNodes: HierarchyNode[],
	workplaceId: number
): HierarchyNode | undefined =>
	hierarchyNodes.find(
		(node) => node.type === 'workplace' && Number(node.childId) === workplaceId
	);

const getHierarchyNodeById = (
	hierarchyNodes: HierarchyNode[],
	hierarchyId: number | undefined | null
): HierarchyNode | undefined => {
	if (hierarchyId == null) return undefined;
	return hierarchyNodes.find((node) => node.id === hierarchyId);
};

const isWorkplaceHierarchyType = (hierarchy: User['hierarchy']): boolean =>
	hierarchy?.hierarchyTypeChild === WORKPLACE_HIERARCHY_TYPE;

/**
 * Returns the workplace ID a user belongs to when assigned to a workplace hierarchy node.
 */
export const getUserHomeWorkplaceId = (
	user: User,
	hierarchyNodes: HierarchyNode[]
): number | null => {
	const hierarchyId = user.hierarchy?.id;
	const node = getHierarchyNodeById(hierarchyNodes, hierarchyId);

	if (node?.type === 'workplace') {
		const candidate = node.childId ?? node.id;
		const parsed = Number(candidate);
		return Number.isNaN(parsed) ? null : parsed;
	}

	if (isWorkplaceHierarchyType(user.hierarchy) && user.hierarchy?.childId != null) {
		const parsed = Number(user.hierarchy.childId);
		return Number.isNaN(parsed) ? null : parsed;
	}

	return null;
};

export const isUserSharedWithWorkplace = (user: User, workplaceId: number): boolean =>
	getWorkplaceIdFromIri(user.sharedEmployeeWorkplace) === workplaceId;

/**
 * True when the user's hierarchy is a custom/organization node above the current workplace.
 */
export const isAboveWorkplaceUser = (
	user: User,
	workplaceHierarchyNodeId: number,
	hierarchyNodes: HierarchyNode[]
): boolean => {
	const userHierarchyId = user.hierarchy?.id;
	if (userHierarchyId == null) return false;

	const ancestors = getAncestorIds(workplaceHierarchyNodeId, hierarchyNodes);
	const aboveNodeIds = ancestors.slice(0, -1);

	return aboveNodeIds.includes(userHierarchyId);
};

export interface FilterUserSettingsUsersOptions {
	workplaceId: number;
	hierarchyNodes: HierarchyNode[];
	showAboveWorkplaceUsers: boolean;
}

/**
 * Filters users for the User Settings list based on the currently selected workplace.
 *
 * Default: current workplace users + users shared with the current workplace.
 * Optional: include users assigned to hierarchy nodes above the current workplace.
 */
export const filterUserSettingsUsers = (
	users: User[],
	{ workplaceId, hierarchyNodes, showAboveWorkplaceUsers }: FilterUserSettingsUsersOptions
): User[] => {
	const workplaceNode = findWorkplaceHierarchyNode(hierarchyNodes, workplaceId);

	return users.filter((user) => {
		const homeWorkplaceId = getUserHomeWorkplaceId(user, hierarchyNodes);
		if (homeWorkplaceId === workplaceId) return true;
		if (isUserSharedWithWorkplace(user, workplaceId)) return true;

		if (showAboveWorkplaceUsers && workplaceNode) {
			return isAboveWorkplaceUser(user, workplaceNode.id, hierarchyNodes);
		}

		return false;
	});
};
